import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import accountTypesAPI from '../../api/accountTypesAPI';
import chartOfAccountsAPI from '../../api/chartOfAccountsAPI';
import generalLedgerAPI from '../../api/generalLedgerAPI';
import Layout from '../../components/Layout';
import ConfirmModal from '../../components/ConfirmModal';
import notify from '../../lib/notify';

const ChartOfAccountEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [account, setAccount] = useState(null);
  const [errors, setErrors] = useState({});
  const [parentAccounts, setParentAccounts] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    accountNumber: '',
    accountName: '',
    accountType: '',
    description: '',
    normalBalance: 'debit',
    parentAccount: null, // Initialize as null instead of empty string
    isActive: true,
    hasBudget: false,
    budgetAmount: 0,
    segment: null,
    statementSection: null,
  });

  const [hasGLActivity, setHasGLActivity] = useState(false);
  const [segmentConfirm, setSegmentConfirm] = useState({ open: false, value: null });
  const [sectionConfirm, setSectionConfirm] = useState({ open: false, value: null });

  const isSystemAccount = Boolean(account?.isSystemAccount);
  const isBillingTenant = localStorage.getItem('tenantType') === 'billing';

  useEffect(() => {
    fetchAccountData();
    fetchDropdownData();
  }, [id]);

  const fetchAccountData = async () => {
    try {
      setLoading(true);
      const response = await chartOfAccountsAPI.getById(id);
      console.log('Account data response:', response);
      
      if (response.success) {
        setAccount(response.data);
        setFormData({
          accountNumber: response.data.accountNumber,
          accountName: response.data.accountName,
          accountType: response.data.accountType?._id || response.data.accountType || '',
          description: response.data.description || '',
          normalBalance: response.data.normalBalance,
          // Use null if no parent account, otherwise use the ID
          parentAccount: response.data.parentAccount?._id || response.data.parentAccount || null,
          isActive: response.data.isActive,
          hasBudget: response.data.hasBudget || false,
          budgetAmount: response.data.budgetAmount || 0,
          segment: response.data.segment || null,
          statementSection: response.data.statementSection || null,
        });

        try {
          const glRes = await generalLedgerAPI.getByAccount(id, { limit: 1 });
          setHasGLActivity((glRes?.pagination?.total || 0) > 0);
        } catch (glErr) {
          console.error('Error checking GL activity for account:', glErr);
        }
      } else {
        throw new Error(response.message || 'Failed to fetch account');
      }
    } catch (error) {
      console.error('Error fetching account:', error);
      setErrors({ general: error.message || 'Failed to load account data' });
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdownData = async () => {
    try {
      //console.log('Starting to fetch dropdown data...');
      
      // Fetch account types
      const typesResponse = await accountTypesAPI.getAll({ activeOnly: true });
      //console.log('Account types API response:', typesResponse);
      
      if (typesResponse && typesResponse.success) {
        setAccountTypes(typesResponse.data || []);
      } else if (Array.isArray(typesResponse)) {
        setAccountTypes(typesResponse);
      } else {
        console.error('Unexpected account types response structure:', typesResponse);
        setErrors(prev => ({ 
          ...prev, 
          dropdown: 'Unexpected response format from account types API' 
        }));
      }

      // Fetch parent accounts
      const parentResponse = await chartOfAccountsAPI.getAll({ 
        limit: 1000, 
        activeOnly: true 
      });
      console.log('Parent accounts response:', parentResponse);
      
      if (parentResponse && parentResponse.success) {
        const filteredParents = (parentResponse.data || []).filter(acc => acc._id !== id);
        setParentAccounts(filteredParents);
      } else if (Array.isArray(parentResponse)) {
        const filteredParents = parentResponse.filter(acc => acc._id !== id);
        setParentAccounts(filteredParents);
      }
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
      setErrors(prev => ({ 
        ...prev, 
        dropdown: `Failed to load dropdown data: ${error.message}` 
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let processedValue = type === 'checkbox' ? checked : value;
    
    // Special handling for parentAccount - convert empty string to null
    if (name === 'parentAccount') {
      processedValue = value === '' ? null : value;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // ✅ Frontend validation: check for duplicate account name (case-insensitive)
    const checkDuplicateAccountName = () => {
      const trimmedName = formData.accountName?.trim().toLowerCase();
      if (!trimmedName) return null;
      
      const duplicate = parentAccounts.find(
        (account) => account._id !== id && account.accountName?.trim().toLowerCase() === trimmedName
      );
      
      return duplicate ? duplicate.accountName : null;
    };

    // ✅ Frontend validation: check for duplicate account code
    const checkDuplicateAccountCode = () => {
      const accountNum = formData.accountNumber?.trim();
      if (!accountNum) return null;
      
      const duplicate = parentAccounts.find(
        (account) => account._id !== id && account.accountNumber === accountNum
      );
      
      return duplicate ? duplicate.accountNumber : null;
    };

    // Check for duplicate account name
    const duplicateName = checkDuplicateAccountName();
    if (duplicateName) {
      newErrors.accountName = `An account named "${duplicateName}" already exists (case-insensitive)`;
    }

    // Check for duplicate account code
    const duplicateCode = checkDuplicateAccountCode();
    if (duplicateCode) {
      newErrors.accountNumber = `Account number "${duplicateCode}" already exists`;
    }

    if (!formData.accountNumber.trim()) {
      newErrors.accountNumber = 'Account number is required';
    } else if (!/^\d{1,6}$/.test(formData.accountNumber)) {
      newErrors.accountNumber = 'Account number must be 1-6 digits';
    }

    if (!formData.accountName.trim()) {
      newErrors.accountName = 'Account name is required';
    }

    if (!formData.accountType) {
      newErrors.accountType = 'Account type is required';
    }

    if (!formData.normalBalance) {
      newErrors.normalBalance = 'Normal balance is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ Helper functions for real-time duplicate checking (exported for JSX use)
  const checkDuplicateAccountNameRT = () => {
    const trimmedName = formData.accountName?.trim().toLowerCase();
    if (!trimmedName) return null;
    
    const duplicate = parentAccounts.find(
      (account) => account._id !== id && account.accountName?.trim().toLowerCase() === trimmedName
    );
    
    return duplicate ? duplicate.accountName : null;
  };

  const checkDuplicateAccountCodeRT = () => {
    const accountNum = formData.accountNumber?.trim();
    if (!accountNum) return null;
    
    const duplicate = parentAccounts.find(
      (account) => account._id !== id && account.accountNumber === accountNum
    );
    
    return duplicate ? duplicate.accountNumber : null;
  };

  // Original validateForm before duplicate checks
  const validateFormOriginal = () => {
    const newErrors = {};

    if (!formData.accountNumber.trim()) {
      newErrors.accountNumber = 'Account number is required';
    } else if (!/^\d{1,6}$/.test(formData.accountNumber)) {
      newErrors.accountNumber = 'Account number must be 1-6 digits';
    }

    if (!formData.accountName.trim()) {
      newErrors.accountName = 'Account name is required';
    }

    if (!formData.accountType) {
      newErrors.accountType = 'Account type is required';
    }

    if (!formData.normalBalance) {
      newErrors.normalBalance = 'Normal balance is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setSaving(true);

      let submitData;

      if (isSystemAccount) {
        submitData = {
          description: formData.description,
          isActive: formData.isActive,
          hasBudget: formData.hasBudget,
          budgetAmount: formData.budgetAmount
        };
      } else {
        submitData = {
          ...formData,
          // Ensure we don't send empty string for parentAccount
          parentAccount: formData.parentAccount === '' ? null : formData.parentAccount,
          segment: formData.segment || null,
          statementSection: formData.statementSection || null,
        };
      }

      console.log('Submitting data:', submitData); // Debug log

      const response = await chartOfAccountsAPI.update(id, submitData);

      if (response.success) {
        notify.success('Account updated successfully!');
        navigate('/client/chart-of-accounts/' + id);
      } else {
        setErrors({ general: response.message || 'Failed to update account' });
      }
    } catch (error) {
      console.error('Error updating account:', error);
      setErrors({ general: error.message || 'Error updating account' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/client/chart-of-accounts');
  };

  const applySegmentChange = (value) => {
    setFormData((prev) => ({ ...prev, segment: value || null }));
  };

  const handleSegmentChange = (e) => {
    const value = e.target.value || null;

    if (hasGLActivity && value !== (account?.segment || null)) {
      setSegmentConfirm({ open: true, value });
      return;
    }

    applySegmentChange(value);
  };

  const confirmSegmentChange = () => {
    applySegmentChange(segmentConfirm.value);
    setSegmentConfirm({ open: false, value: null });
  };

  const applySectionChange = (value) => {
    setFormData((prev) => ({ ...prev, statementSection: value || null }));
  };

  const handleSectionChange = (e) => {
    const value = e.target.value || null;

    if (hasGLActivity && value !== (account?.statementSection || null)) {
      setSectionConfirm({ open: true, value });
      return;
    }

    applySectionChange(value);
  };

  const confirmSectionChange = () => {
    applySectionChange(sectionConfirm.value);
    setSectionConfirm({ open: false, value: null });
  };

  // Enhanced debug component
  /*
  const DebugInfo = () => (
    <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
      <h4 className="font-medium text-yellow-800">Debug Info:</h4>
      <p>Account Types Count: {accountTypes.length}</p>
      <p>Parent Accounts Count: {parentAccounts.length}</p>
      <p>Form Parent Account Value: "{formData.parentAccount}" (type: {typeof formData.parentAccount})</p>
      <p>Form Account Type Value: {formData.accountType}</p>
      <div className="mt-2">
        <p className="text-sm font-medium">Form Data:</p>
        <pre className="text-xs mt-1 overflow-auto">
          {JSON.stringify({
            accountNumber: formData.accountNumber,
            accountName: formData.accountName,
            accountType: formData.accountType,
            parentAccount: formData.parentAccount,
            normalBalance: formData.normalBalance
          }, null, 2)}
        </pre>
      </div>
    </div>
  );*/

  const selectedAccountTypeCategory = accountTypes.find((t) => t._id === formData.accountType)?.category;
  const showSegmentField = isBillingTenant && ["income", "expense"].includes(selectedAccountTypeCategory);
  const showStatementSectionField = ["income", "expense"].includes(selectedAccountTypeCategory);
  const isRevenueCategory = selectedAccountTypeCategory === "income";
  const statementSectionOptions = isRevenueCategory
    ? [
        { value: "", label: "Revenue" },
        { value: "OTHER_INCOME_EXPENSE", label: "Other income (expenses)" },
      ]
    : [
        { value: "", label: "Operating expense" },
        { value: "COST_OF_SERVICES", label: "Cost of services" },
        { value: "OTHER_INCOME_EXPENSE", label: "Other income (expenses)" },
      ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <Layout pageTitle="Edit Chart of Accounts">
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
         <p className="mt-2 text-sm text-gray-600">
            Update account information and settings
          </p>
        </div>

        {/* Error Alerts */}
        {errors.general && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-1 text-sm text-red-700">
                  <p>{errors.general}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {errors.dropdown && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Warning</h3>
                <div className="mt-1 text-sm text-yellow-700">
                  <p>{errors.dropdown}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white shadow-sm rounded-lg">
          <div className="px-6 py-6 space-y-6">
            {/* Basic Information Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700">
                    Account Number *
                  </label>
                  <input
                    type="text"
                    id="accountNumber"
                    name="accountNumber"
                    value={formData.accountNumber}
                    onChange={handleInputChange}
                    disabled={isSystemAccount}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                      errors.accountNumber || checkDuplicateAccountCodeRT() ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    } ${
                      isSystemAccount ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                    }`}
                    placeholder="e.g., 1001"
                  />
                  {isSystemAccount && (
                    <p className="mt-1 text-xs text-gray-500">System account field is read-only</p>
                  )}
                  {checkDuplicateAccountCodeRT() && (
                    <p className="mt-1 text-xs text-red-600 font-semibold">
                      ✕ Account number "{checkDuplicateAccountCodeRT()}" already exists
                    </p>
                  )}
                  {!checkDuplicateAccountCodeRT() && errors.accountNumber && (
                    <p className="mt-1 text-sm text-red-600">{errors.accountNumber}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="accountName" className="block text-sm font-medium text-gray-700">
                    Account Name *
                  </label>
                  <input
                    type="text"
                    id="accountName"
                    name="accountName"
                    value={formData.accountName}
                    onChange={handleInputChange}
                    disabled={isSystemAccount}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                      errors.accountName || checkDuplicateAccountNameRT() ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    } ${
                      isSystemAccount ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                    }`}
                    placeholder="e.g., Cash and Cash Equivalents"
                  />
                  {isSystemAccount && (
                    <p className="mt-1 text-xs text-gray-500">System account field is read-only</p>
                  )}
                  {checkDuplicateAccountNameRT() && (
                    <p className="mt-1 text-xs text-red-600 font-semibold">
                      ✕ An account with this name already exists (case-insensitive)
                    </p>
                  )}
                  {!checkDuplicateAccountNameRT() && errors.accountName && (
                    <p className="mt-1 text-sm text-red-600">{errors.accountName}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Classification Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Classification</h3>
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {/* Account Type Dropdown */}
                <div>
                  <label htmlFor="accountType" className="block text-sm font-medium text-gray-700">
                    Account Type *
                  </label>
                  <select
                    id="accountType"
                    name="accountType"
                    value={formData.accountType}
                    onChange={handleInputChange}
                    disabled={isSystemAccount}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                      errors.accountType ? 'border-red-300' : 'border-gray-300'
                    } ${
                      isSystemAccount ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                    }`}
                  >
                    <option value="">Select Account Type</option>
                    {accountTypes.map(type => (
                      <option key={type._id} value={type._id}>
                        {type.name} {type.code && `(${type.code})`}
                      </option>
                    ))}
                  </select>
                  {isSystemAccount && (
                    <p className="mt-1 text-xs text-gray-500">System account field is read-only</p>
                  )}
                  {errors.accountType && (
                    <p className="mt-1 text-sm text-red-600">{errors.accountType}</p>
                  )}
                  {accountTypes.length === 0 && !errors.dropdown && (
                    <p className="mt-1 text-sm text-yellow-600">No account types available</p>
                  )}
                </div>

                {/* Normal Balance */}
                <div>
                  <label htmlFor="normalBalance" className="block text-sm font-medium text-gray-700">
                    Normal Balance *
                  </label>
                  <select
                    id="normalBalance"
                    name="normalBalance"
                    value={formData.normalBalance}
                    onChange={handleInputChange}
                    disabled={isSystemAccount}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                      errors.normalBalance ? 'border-red-300' : 'border-gray-300'
                    } ${
                      isSystemAccount ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                    }`}
                  >
                    <option value="debit">Debit</option>
                    <option value="credit">Credit</option>
                  </select>
                  {isSystemAccount && (
                    <p className="mt-1 text-xs text-gray-500">System account field is read-only</p>
                  )}
                  {errors.normalBalance && (
                    <p className="mt-1 text-sm text-red-600">{errors.normalBalance}</p>
                  )}
                </div>

                {showSegmentField && (
                  <div>
                    <label htmlFor="segment" className="block text-sm font-medium text-gray-700">
                      Segment
                    </label>
                    <select
                      id="segment"
                      name="segment"
                      value={formData.segment || ''}
                      onChange={handleSegmentChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option value="">Standard</option>
                      <option value="TRUCKING">Trucking</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Reporting-layer classification for income statement segmentation. Does not affect GL postings.
                    </p>
                  </div>
                )}

                {showStatementSectionField && (
                  <div>
                    <label htmlFor="statementSection" className="block text-sm font-medium text-gray-700">
                      Statement section
                    </label>
                    <select
                      id="statementSection"
                      name="statementSection"
                      value={formData.statementSection || ''}
                      onChange={handleSectionChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      {statementSectionOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Which income statement section this account rolls up into.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Hierarchy Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Hierarchy</h3>
              
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label htmlFor="parentAccount" className="block text-sm font-medium text-gray-700">
                    Parent Account
                  </label>
                  <select
                    id="parentAccount"
                    name="parentAccount"
                    value={formData.parentAccount || ''} // Use empty string for "no selection"
                    onChange={handleInputChange}
                    disabled={isSystemAccount}
                    className={`mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                      isSystemAccount ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                    }`}
                  >
                    <option value="">No Parent Account</option>
                    {parentAccounts.map(account => (
                      <option key={account._id} value={account._id}>
                        {account.accountNumber} - {account.accountName}
                      </option>
                    ))}
                  </select>
                  {isSystemAccount && (
                    <p className="mt-1 text-xs text-gray-500">System account field is read-only</p>
                  )}
                  {parentAccounts.length === 0 && !errors.dropdown && (
                    <p className="mt-1 text-sm text-yellow-600">No parent accounts available</p>
                  )}
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h3>
              
              <div className="space-y-6">
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows={3}
                    value={formData.description}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Enter account description..."
                  />
                </div>

                {/* Budget Settings */}
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-md font-medium text-gray-900">Budget Settings</h4>
                      <p className="text-sm text-gray-500">Enable budget tracking for this account</p>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="hasBudget"
                        name="hasBudget"
                        checked={formData.hasBudget}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="hasBudget" className="ml-2 block text-sm text-gray-900">
                        Enable Budget
                      </label>
                    </div>
                  </div>

                  {formData.hasBudget && (
                    <div className="mt-4">
                      <label htmlFor="budgetAmount" className="block text-sm font-medium text-gray-700">
                        Budget Amount
                      </label>
                      <input
                        type="number"
                        id="budgetAmount"
                        name="budgetAmount"
                        value={formData.budgetAmount}
                        onChange={handleInputChange}
                        step="0.01"
                        min="0"
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="0.00"
                      />
                    </div>
                  )}
                </div>

                {/* Status */}
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isActive"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                      Account is active
                    </label>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Inactive accounts won't be available for new transactions
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="px-6 py-4 bg-gray-50 rounded-b-lg border-t border-gray-200 flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || (!isSystemAccount && (checkDuplicateAccountNameRT() || checkDuplicateAccountCodeRT()))}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </span>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>

        {/* Debug Info - Remove in production
        <DebugInfo /> */}

        {/* Read-only Information */}
        {account && (
          <div className="mt-8 bg-white shadow-sm rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">System Information</h3>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">Current Balance</dt>
                <dd className="text-sm text-gray-900">
                  {account.currentBalance?.toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'PHP'
                  })}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Created By</dt>
                <dd className="text-sm text-gray-900">
                  {account.createdBy?.name || 'N/A'} on {new Date(account.createdDate).toLocaleDateString()}
                </dd>
              </div>
              {account.modifiedBy && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Last Modified By</dt>
                  <dd className="text-sm text-gray-900">
                    {account.modifiedBy?.name || 'N/A'} on {new Date(account.modifiedDate).toLocaleDateString()}
                  </dd>
                </div>
              )}
              {account.isSystemAccount && (
                <div className="sm:col-span-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    System Account
                  </span>
                  <p className="mt-1 text-sm text-gray-500">
                    This is a system account and some fields cannot be modified
                  </p>
                </div>
              )}
            </dl>
          </div>
        )}
      </div>
    </div>

    <ConfirmModal
      isOpen={segmentConfirm.open}
      title="Change segment?"
      message="Changing segment reclassifies all historical income statements."
      confirmLabel="Change segment"
      variant="danger"
      onConfirm={confirmSegmentChange}
      onCancel={() => setSegmentConfirm({ open: false, value: null })}
    />

    <ConfirmModal
      isOpen={sectionConfirm.open}
      title="Change statement section?"
      message="Changing the statement section reclassifies all historical income statements."
      confirmLabel="Change section"
      variant="danger"
      onConfirm={confirmSectionChange}
      onCancel={() => setSectionConfirm({ open: false, value: null })}
    />
    </Layout>
  );
};

export default ChartOfAccountEdit;